import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const RakutenEventTemplateList = () => {
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  // 🌟 State管理
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editorContent, setEditorContent] = useState('');
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [dragIndex, setDragIndex] = useState(null);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/rakuten-event/templates`);
      const data = await res.json();
      const safeData = Array.isArray(data) ? data : (data.data || []);
      setTemplates(safeData);

      if (safeData.length > 0 && !selectedTemplate) {
        handleSelectTemplate(safeData[0]);
      }
    } catch (error) {
      console.error("テンプレート取得エラー:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setEditorContent(template.page_template || '');
  };

  const handleCreateNew = async (e) => {
    e.preventDefault();
    if (!newTemplateName) return;

    try {
      const res = await fetch(`${API_URL}/rakuten-event/templates/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newTemplateName,
          page_template: editorContent
        })
      });

      if (res.ok) {
        setShowModal(false);
        setNewTemplateName('');
        const newTemp = await res.json();
        await fetchTemplates();
        handleSelectTemplate(newTemp);
      } else {
        alert("新規作成に失敗しました。名前が重複している可能性があります。");
      }
    } catch (error) {
      console.error("新規作成エラー:", error);
    }
  };

  const handleUpdate = async () => {
    if (!selectedTemplate) return;
    if (!window.confirm("この内容で上書き保存しますか？")) return;

    try {
      const res = await fetch(`${API_URL}/rakuten-event/templates/${selectedTemplate.uuid}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: selectedTemplate.name,
          page_template: editorContent
        })
      });

      if (res.ok) {
        alert("上書き保存しました！");
        fetchTemplates();
      } else {
        alert("保存に失敗しました。");
      }
    } catch (error) {
      console.error("更新エラー:", error);
    }
  };

  const handleDelete = async () => {
    if (!selectedTemplate) return;
    if (!window.confirm(`「${selectedTemplate.name}」を削除してよろしいですか？`)) return;

    try {
      const res = await fetch(`${API_URL}/rakuten-event/templates/${selectedTemplate.uuid}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        setSelectedTemplate(null);
        setEditorContent('');
        fetchTemplates();
      } else {
        alert("削除に失敗しました。");
      }
    } catch (error) {
      console.error("削除エラー:", error);
    }
  };

  const handleDragStart = (index) => {
    setDragIndex(index);
  };

  const handleDragEnter = (index) => {
    if (dragIndex === index) return;
    const newTemplates = [...templates];
    const draggedItem = newTemplates[dragIndex];
    newTemplates.splice(dragIndex, 1);
    newTemplates.splice(index, 0, draggedItem);

    setDragIndex(index);
    setTemplates(newTemplates);
  };

  const handleDragEnd = async () => {
    setDragIndex(null);
    const sortData = templates.map((temp, index) => ({
      uuid: temp.uuid,
      serial_num: index + 1
    }));

    try {
      const res = await fetch(`${API_URL}/rakuten-event/templates/sort`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sortData)
      });

      if (!res.ok) {
        alert("並べ替えの保存に失敗しました。");
        fetchTemplates();
      }
    } catch (error) {
      console.error("並べ替え保存エラー:", error);
      fetchTemplates();
    }
  };

  // 🌟 ここを <> から <div> に変更しました
  return (
    <div>
      <ol className="breadcrumb float-xl-end">
        <li className="breadcrumb-item"><Link to="/">Home</Link></li>
        <li className="breadcrumb-item"><Link to="/rakuten-event/pages">楽天イベントページ</Link></li>
        <li className="breadcrumb-item active">テンプレート管理</li>
      </ol>
      <h1 className="page-header">テンプレート管理</h1>

      <div className="row">
        <div className="col-md-3">
          <div className="panel panel-inverse">
            <div className="panel-heading">
              <h4 className="panel-title">テンプレート一覧</h4>
              <div className="panel-heading-btn">
                <button
                  className="btn btn-xs btn-icon btn-circle btn-success"
                  onClick={fetchTemplates}
                  title="一覧をリロード"
                >
                  <i className="fa fa-redo"></i>
                </button>
              </div>
            </div>
            <div className="panel-body p-0">
              {loading ? (
                <div className="text-center p-3"><i className="fa fa-spinner fa-spin"></i></div>
              ) : (
                <table className="table table-hover mb-0">
                  <tbody>
                    {templates.map((temp, index) => (
                      <tr
                        key={temp.uuid}
                        // 🌟 ここを変更！選択中の行は青色（table-primary）になり、太字になります
                        className={selectedTemplate?.uuid === temp.uuid ? 'text-primary fw-bold' : ''}
                        style={{
                          cursor: 'grab',
                          // 🌟 選択中の時だけ、ホバーしても黒くならない優しい薄青色を背景に設定
                          backgroundColor: selectedTemplate?.uuid === temp.uuid ? '#e6f2ff' : 'transparent'
                        }}
                        onClick={() => handleSelectTemplate(temp)}
                        draggable="true"
                        onDragStart={() => handleDragStart(index)}
                        onDragEnter={() => handleDragEnter(index)}
                        onDragEnd={handleDragEnd}
                        onDragOver={(e) => e.preventDefault()}
                      >
                        <td width="1%" className="text-center text-nowrap text-muted ps-3">
                          <i className="fa fa-bars"></i>
                        </td>
                        <td>{temp.name}</td>
                        <td width="1%"><i className="fa fa-chevron-right text-muted"></i></td>
                      </tr>
                    ))}
                    {templates.length === 0 && (
                      <tr><td colSpan="3" className="text-center p-3">データがありません</td></tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        <div className="col-md-9">
          <div className="panel panel-inverse">
            <div className="panel-heading">
              <h4 className="panel-title">
                {selectedTemplate ? `編集中: ${selectedTemplate.name}` : '新規作成'}
              </h4>
            </div>

            <div className="panel-toolbar text-end">
              <button className="btn btn-white btn-sm me-2" onClick={() => setShowModal(true)}>
                新規保存 (別名で保存)
              </button>
              {selectedTemplate && (
                <>
                  <button className="btn btn-primary btn-sm me-2" onClick={handleUpdate}>
                    <i className="fa fa-save me-1"></i>上書き保存
                  </button>
                  <button className="btn btn-danger btn-sm" onClick={handleDelete}>
                    削除
                  </button>
                </>
              )}
            </div>

            <div className="p-0">
              <textarea
                className="form-control border-0 rounded-0 bg-light p-3"
                style={{ fontFamily: 'monospace', fontSize: '14px', resize: 'vertical' }}
                rows="25"
                placeholder="HTMLテンプレートのコードをここに入力..."
                value={editorContent}
                onChange={(e) => setEditorContent(e.target.value)}
                disabled={!selectedTemplate && templates.length > 0}
              ></textarea>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">テンプレートの新規作成</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <form onSubmit={handleCreateNew}>
                <div className="modal-body">
                  <div className="form-group row">
                    <label className="col-md-4 col-form-label">テンプレート名</label>
                    <div className="col-md-8">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="例: お買い物マラソン用ベース"
                        value={newTemplateName}
                        onChange={(e) => setNewTemplateName(e.target.value)}
                        required
                      />
                      <small className="text-muted d-block mt-1">
                        ※現在のエディタの内容が初期値としてコピーされます。
                      </small>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>閉じる</button>
                  <button type="submit" className="btn btn-primary">作成</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RakutenEventTemplateList;